import type { ReactNode } from 'react';

import { Icon } from '#/components/Icon/Icon';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';

import { Container, IconContainer, StarContainer } from './StarRating.styled';

function TOOLTIP_TEXT(i18n: I18nFn) {
  return [
    i18n('Dislike it'),
    i18n('Could be better'),
    i18n('Just okay'),
    i18n('Like it!'),
    i18n('Love it!'),
  ];
}

function TooltipContainer({
  children,
  disableTooltip,
  index,
}: {
  children: ReactNode;
  disableTooltip: boolean;
  index: number;
}) {
  const { i18n } = useI18n();

  if (disableTooltip) {
    return <div>{children}</div>;
  }
  return (
    <Tooltip variant="filled" content={TOOLTIP_TEXT(i18n)[index]}>
      {children}
    </Tooltip>
  );
}

type Props = {
  className?: string;
  disabled?: boolean;
  disableTooltip?: boolean;
  iconSize?: number;
  qaIdPrefix: string;
  rating: number | null;
  setRating?: (rating: number) => void;
};

export function StarRating({
  className,
  disabled = false,
  disableTooltip = false,
  iconSize,
  qaIdPrefix,
  rating,
  setRating,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Container
      className={className}
      role="radiogroup"
      data-qa-id={`${qaIdPrefix}-container`}
    >
      {[...Array(5).keys()].map((i) => (
        <StarContainer
          key={i}
          aria-label={disableTooltip ? String(i + 1) : TOOLTIP_TEXT(i18n)[i]}
          aria-checked={rating === i + 1 ? 'true' : 'false'}
          data-qa-id={`${qaIdPrefix}-${i + 1}`}
          data-qa-index={i + 1}
          $disabled={disabled}
          $filled={rating ? i < rating : false}
          onClick={() => !disabled && setRating?.(i + 1)}
          role={disabled ? 'none' : 'radio'}
        >
          <TooltipContainer
            disableTooltip={disabled || disableTooltip}
            index={i}
          >
            <IconContainer>
              <div className="star-filled">
                <Icon name="star-filled" size={iconSize ?? 27} />
              </div>
              <div className="star-outline">
                <Icon name="star-outline" size={iconSize ?? 27} />
              </div>
            </IconContainer>
          </TooltipContainer>
        </StarContainer>
      ))}
    </Container>
  );
}
